// SPDX-License-Identifier: BUSL-1.1

/**
 * Application shell layout — sidebar + topbar + content area.
 *
 * Wraps all authenticated routes. The sidebar is fixed-width on the left,
 * the topbar spans the content area, and the main content scrolls
 * independently.
 *
 */

import { Outlet, useNavigate } from 'react-router';
import { ChevronsUpDown, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ActorAvatar } from '@/components/actor-avatar';
import { SidebarNav } from '@/components/sidebar-nav';
import { OrgSwitcher } from '@/components/org-switcher';

export function AppShell() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // TODO(DASH-03-backend): pull from /api/keys/me when it exists.
  const user = { name: 'Operator', role: 'admin' };

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="flex h-screen bg-neutral-950 text-neutral-100">
      {/* Sidebar */}
      <aside className="flex w-56 flex-col border-r border-neutral-800 p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="font-mono text-lg font-bold tracking-tight">
            build.ceo<span className="animate-pulse text-emerald-400">_</span>
          </div>
          <OrgSwitcher />
        </div>

        <SidebarNav />

        <div className="mt-auto border-t border-neutral-800 pt-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  className="flex w-full items-center gap-2 px-3 hover:bg-neutral-800/50"
                />
              }
            >
              <ActorAvatar id={user.name} size="sm" />
              <div className="flex flex-1 flex-col items-start text-left">
                <span className="text-xs font-medium text-neutral-200">
                  {user.name}
                </span>
                <span className="text-[10px] text-neutral-500">
                  {user.role}
                </span>
              </div>
              <ChevronsUpDown className="size-3 text-neutral-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 size-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto p-6">
        <Outlet />
      </main>
    </div>
  );
}
